/* eslint-disable react/jsx-curly-brace-presence */
import React, { useMemo } from "react";
import { differenceInMinutes, format } from "date-fns";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Button } from "@mui/material";
import { styled } from "@iventis/styles";
import { utcToZonedTime } from "date-fns-tz";
import { getContrastTextColour } from "@iventis/utilities";
import { Content } from "@iventis/translations/content/typed-content";
import { borderRadius } from "@iventis/styles/src/atomic-rules";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { getCalendarSubSessionColour, getEventBlockDisplayValuesFromDataFieldValues } from "./event-block-helpers";

/**
 * Gets the code, description and colour to be displayed
 *
 * @param eventBlock Event unit (sub event)
 * @param eventBlockHierarchy Session as hierarchy
 */
export const getSubEventDisplayDetails = (
    eventBlock: EventBlock,
    eventBlockHierarchy: Hierarchy<EventBlock>
): {
    code: string;
    description: string;
    colour: {
        fill: string;
        outline: string;
    };
} => {
    // Inherit colour from session at the top of the hierarchy
    const colour = getCalendarSubSessionColour(eventBlockHierarchy.item, eventBlock);
    const { code, displayText: description } = getEventBlockDisplayValuesFromDataFieldValues(eventBlock.dataFieldValues, eventBlock.type.dataFields);
    return { code, colour, description };
};

export const SubEventBlockCardContent: React.FC<{
    eventBlock: EventBlock;
    eventBlockHierarchy: Hierarchy<EventBlock>;
    onEdit?: (id: string) => void;
    canEditEvent: boolean;
    timezone: string;
}> = ({ eventBlock, eventBlockHierarchy, onEdit, canEditEvent, timezone }) => {
    const { code, description, colour } = useMemo(() => getSubEventDisplayDetails(eventBlock, eventBlockHierarchy), [eventBlock]);
    const translate = useIventisTranslate();

    return (
        <StyledBox colours={colour}>
            <span>
                <StyledRow>
                    <p>
                        {format(utcToZonedTime(eventBlock.startAt, timezone), "HH:mm")} - {format(utcToZonedTime(eventBlock.endAt, timezone), "HH:mm")} (
                        {differenceInMinutes(new Date(eventBlock.endAt), new Date(eventBlock.startAt)).toString()}
                        {"'"})
                    </p>
                </StyledRow>
                <StyledRow>
                    <EventUnitDescription>{description}</EventUnitDescription>
                </StyledRow>
                <StyledRow>
                    <SessionCode>{code}</SessionCode>
                </StyledRow>
            </span>
            {canEditEvent && (
                <Button style={{ color: getContrastTextColour(colour.fill) }} onClick={() => onEdit(eventBlock.id)}>
                    {translate(Content.schedule.session_card.edit)}
                </Button>
            )}
        </StyledBox>
    );
};

const StyledBox = styled.div<{ colours: { fill: string; outline: string } }>`
    display: flex;
    margin-block: 8px;
    padding: 5px;
    justify-content: space-between;
    border-radius: ${borderRadius.standard};
    color: ${(props) => getContrastTextColour(props.colours.fill)};
    background-color: ${(prop) => prop.colours.fill};
    outline-color: ${(prop) => prop.colours.outline};
    outline-style: solid;
    outline-width: 2px;
`;

const StyledRow = styled.div`
    padding-top: 2.5px;
    padding-bottom: 2.5px;
`;

const EventUnitDescription = styled.div`
    font-weight: 500;
`;

const SessionCode = styled.div`
    padding-right: 12px;
`;
